import { Component, OnInit } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { AuctionReportLineItem } from '../../model/auction.model';
import { AuctionModelService } from '../../model/auction.model';
import { NgForm } from '@angular/forms';
import { AddBidderDialogComponent } from '../add-bidder-dialog/add-bidder-dialog.component';
import { PortalUser } from '../../model/portaluser.model';

export interface SplitUser {
  amount: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string[];
  user?: PortalUser;
}

@Component({
  selector: 'app-auction-report-split-dialog',
  templateUrl: './auction-report-split-dialog.component.html',
  styleUrls: ['./auction-report-split-dialog.component.scss']
})
export class AuctionReportSplitDialogComponent implements OnInit {
  public lineItem: AuctionReportLineItem;
  public processing: boolean = false;
  public splitUsers: SplitUser[] = [];
  public originalBuyerAmount: number | null = null;
  public totalSplit: number = 0;
  public isValidSplit: boolean = false;

  constructor(
    public ref: NbDialogRef<AuctionReportSplitDialogComponent>,
    private auctionModel: AuctionModelService,
    private dialogService: NbDialogService
  ) { }

  ngOnInit(): void {
    // Initialize with the original winning bidder amount (full price by default)
    this.originalBuyerAmount = this.lineItem.finalPrice;
    // Start with empty split users array - user can add buyers using the + button
    this.splitUsers = [];
    this.calculateTotal();
  }

  public calculateTotal(): void {
    this.totalSplit = 0;
    if (this.originalBuyerAmount) {
      this.totalSplit += this.originalBuyerAmount;
    }
    this.splitUsers.forEach(user => {
      if (user.amount) {
        this.totalSplit += user.amount;
      }
    });
    this.isValidSplit = Math.abs(this.totalSplit - this.lineItem.finalPrice) < 0.01;
  }

  public onOriginalBuyerAmountChange(): void {
    this.calculateTotal();
  }

  public onSplitUserAmountChange(index: number): void {
    this.calculateTotal();
  }

  public addSplitUser(): void {
    this.splitUsers.push({
      amount: null,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: ['', '']
    });
  }

  public removeSplitUser(index: number): void {
    this.splitUsers.splice(index, 1);
    this.calculateTotal();
  }

  public openUserLookup(index: number): void {
    this.dialogService.open(AddBidderDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {}
    }).onClose.subscribe((result: any) => {
      if (result && result.parms && result.parms.length > 0) {
        const selectedUser: PortalUser = result.parms[0];
        this.splitUsers[index].user = selectedUser;
        this.splitUsers[index].firstName = selectedUser.givenName || '';
        this.splitUsers[index].lastName = selectedUser.familyName || '';
        this.splitUsers[index].email = selectedUser.email || '';
        this.splitUsers[index].phoneNumber = selectedUser.phone || '';
        if (selectedUser.physicalAddress) {
          const cityStateZip = [
            selectedUser.physicalCity || '',
            selectedUser.physicalState || '',
            selectedUser.physicalZip || ''
          ].filter(Boolean).join(', ');
          this.splitUsers[index].address = [
            ...selectedUser.physicalAddress,
            cityStateZip
          ];
        }
      }
    });
  }

  public async saveSplit(f: NgForm): Promise<void> {
    if (!this.isValidSplit) {
      return;
    }

    this.processing = true;
    try {
      // Prepare split data
      const splitData = {
        originalBuyerAmount: this.originalBuyerAmount,
        splitUsers: this.splitUsers.map(user => ({
          amount: user.amount,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phoneNumber: user.phoneNumber,
          address: user.address
        }))
      };

      await this.auctionModel.activeAuctionReport.splitLineItem(this.lineItem, splitData);
      // Refresh the auction report to show the new split line items
      // This will need to be handled by the parent component
      this.ref.close({ success: true });
    } catch (e) {
      console.error("Error saving the split invoice.", e);
      alert('Error saving split. Please ensure the backend split mutation is implemented.');
    } finally {
      this.processing = false;
    }
  }
}
